import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { ButtonComponent, ButtonVariant } from "@sxm-content-creators/tools-ds";

/** A run of message text rendered in a `<strong>` when `bold` is set. */
export interface ConfirmMessageSegment {
    text: string;
    bold?: boolean;
}

export interface ConfirmModalData {
    title: string;
    /**
     * A plain string, or a mix of strings and segments when parts of the message should be bold.
     * Parts are rendered back to back with nothing between them, so include any surrounding
     * spaces yourself: `["This ", { text: "permanently", bold: true }, " deletes it."]`.
     */
    message: string | (string | ConfirmMessageSegment)[];
    confirmLabel: string;
    /** Defaults to "Cancel". */
    cancelLabel?: string;
    /** Renders the title in the error color, for destructive actions such as Delete. */
    danger?: boolean;
}

@Component({
    selector: "app-confirm-modal",
    templateUrl: "./confirm-modal.component.html",
    styleUrl: "./confirm-modal.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [ButtonComponent]
})
export class ConfirmModalComponent {
    readonly data = inject<ConfirmModalData>(MAT_DIALOG_DATA);
    private readonly dialogRef = inject<MatDialogRef<ConfirmModalComponent, boolean>>(MatDialogRef);

    protected readonly buttonVariant = ButtonVariant;

    /** The message normalised to segments, so the template has a single shape to render. */
    protected readonly messageSegments: readonly ConfirmMessageSegment[] = (typeof this.data.message === "string"
        ? [this.data.message]
        : this.data.message
    ).map(part => (typeof part === "string" ? { text: part } : part));

    confirm(): void {
        this.dialogRef.close(true);
    }

    cancel(): void {
        this.dialogRef.close(false);
    }
}
